import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ShoppingCart, Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const CartPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const updateQuantity = async (itemId, newQuantity) => {
    try {
      const response = await axios.put(`${API}/cart/item/${itemId}`, { quantity: newQuantity });
      setCart(response.data);
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const removeItem = async (itemId) => {
    try {
      const response = await axios.delete(`${API}/cart/item/${itemId}`);
      setCart(response.data);
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`);
      setCart({ items: [], total: 0 });
      toast.success("Cart cleared");
    } catch (error) {
      toast.error("Failed to clear cart");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <ShoppingCart className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl font-black text-white font-['Outfit']">{t("cart.title")}</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : cart.items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingCart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">{t("cart.empty")}</h2>
              <p className="text-slate-400 mb-6">Start shopping to add items to your cart</p>
              <Link to="/store" className="btn-primary px-6 py-3 inline-block">
                Browse Store
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cart.items.map((item) => (
                  <div key={item.id} className="card-surface p-4 flex gap-4" data-testid={`cart-item-${item.id}`}>
                    <img 
                      src={item.product_image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200"}
                      alt={item.product_name}
                      className="w-24 h-24 object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <Link to={`/store/product/${item.product_id}`} className="text-white font-medium hover:text-yellow-500 transition-colors">
                        {item.product_name}
                      </Link>
                      {item.size && (
                        <p className="text-slate-400 text-sm mt-1">Size: {item.size}</p>
                      )}
                      <p className="text-amber-500 text-sm mt-1 capitalize">{item.category?.replace("_", " ")}</p>
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))}
                            className="w-8 h-8 flex items-center justify-center border border-white/10 text-slate-400 hover:text-white"
                            data-testid={`decrease-${item.id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-white">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center border border-white/10 text-slate-400 hover:text-white"
                            data-testid={`increase-${item.id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-white font-medium">R{(item.price * item.quantity).toFixed(2)}</span>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            data-testid={`remove-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={clearCart}
                  className="text-slate-400 hover:text-red-500 text-sm transition-colors"
                  data-testid="clear-cart"
                >
                  Clear cart
                </button>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="card-surface p-6 sticky top-24">
                  <h2 className="text-lg font-bold text-white mb-4 font-['Outfit']">Order Summary</h2>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between text-slate-400">
                      <span>Subtotal ({cart.items.length} items)</span>
                      <span>R{cart.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                      <span>Shipping</span>
                      <span>{cart.total >= 500 ? "Free" : "R50.00"}</span>
                    </div>
                    <div className="pt-3 border-t border-white/10 flex justify-between text-white font-bold">
                      <span>Total</span>
                      <span>R{(cart.total + (cart.total >= 500 ? 0 : 50)).toFixed(2)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate("/checkout")}
                    className="btn-primary w-full mt-6 py-3 flex items-center justify-center gap-2"
                    data-testid="checkout-btn"
                  >
                    {t("cart.checkout")}
                    <ArrowRight className="w-4 h-4" />
                  </Button>

                  <Link to="/store" className="block text-center text-slate-400 hover:text-white text-sm mt-4 transition-colors">
                    {t("cart.continueShopping")}
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
